/**
 * A horizontal bar chart: one row per category, its label on the left, the bar, and its count.
 *
 * Horizontal because the categories here are words - operating systems, customer names, "8 to 30
 * days" - and a vertical chart would have to tilt them or cut them off. Painted by the control
 * itself, like the sparkline, so it draws at the real pixel size and follows a theme switch.
 *
 * The marks follow the house chart rules: thin bars with rounded ends, a 2px surface gap between
 * stacked segments, values in text ink beside the bar (never inside it in the series colour), and
 * a hover tooltip per row that gives every number, so nothing is readable by colour alone.
 *
 * A row with devices behind it is a click target; the chart marks the `selected` row so the list
 * it opened is visibly tied to its bar.
 */
import {
  Control,
  type Color,
  type ControlOptions,
  type Point,
  type RenderContext,
  type Size,
  type Theme,
  type TooltipContent
} from 'webwin';
import type { BarRow } from '../deviceAnalytics.js';
import type { CrmColors } from '../theme/palette.js';
import { crm } from '../theme/CrmTheme.js';
import { FONT, GAP } from './layout.js';

export interface BarSeries {
  readonly label: string;
  /** A palette key, resolved at paint time so a theme switch repaints it. */
  readonly color: keyof CrmColors;
}

export interface BarChartOptions extends ControlOptions {
  series: readonly BarSeries[];
  /** Clicking a row that counts any device calls this. */
  onRowClick?: (row: BarRow) => void;
}

const ROW_HEIGHT = 26;
const BAR_HEIGHT = 12;
const SEGMENT_GAP = 2;
/** The label column never takes more than this share of the width, however long the labels are. */
const LABEL_SHARE = 0.42;
/** Bold at the label's own size, so the measured label column still fits it. */
const SELECTED_FONT = { ...FONT.small, weight: 600 };

export class BarChart extends Control {
  readonly series: readonly BarSeries[];
  readonly #onRowClick?: (row: BarRow) => void;
  #rows: readonly BarRow[] = [];
  #hover = -1;
  #selected: string | null = null;

  constructor(options: BarChartOptions) {
    super({ hAlign: 'stretch', autoHeight: true, ...options });
    this.series = options.series;
    this.#onRowClick = options.onRowClick;
    this.on('pointerleave', () => this.#setHover(-1));
    this.on('click', e => {
      const row = this.#rows[this.#rowAt(e.y)];
      if (row && isClickable(row)) this.#onRowClick?.(row);
    });
  }

  get rows(): readonly BarRow[] {
    return this.#rows;
  }

  /** The label of the row whose devices are listed, or null. */
  get selected(): string | null {
    return this.#selected;
  }

  set selected(label: string | null) {
    if (label === this.#selected) return;
    this.#selected = label;
    this.invalidate();
  }

  set rows(value: readonly BarRow[]) {
    this.#rows = value;
    this.#hover = -1;
    this.height = value.length * ROW_HEIGHT;
    this.invalidate();
  }

  override preferredSize(available: Size): Size {
    return { width: available.width, height: this.#rows.length * ROW_HEIGHT };
  }

  protected override hitTestSelf(): boolean {
    return true;
  }

  override cursorAt(p: Point): string {
    const row = this.#rows[this.#rowAt(p.y)];
    return row && isClickable(row) && this.#onRowClick ? 'pointer' : 'default';
  }

  override getTooltip(p: Point): TooltipContent | null {
    const index = this.#rowAt(p.y);
    const row = this.#rows[index];
    if (!row) {
      this.#setHover(-1);
      return null;
    }
    this.#setHover(index);
    const total = sum(row.values);
    const all = this.#grandTotal();
    const lines = [`${row.label}: ${total}${all > 0 ? ` (${percent(total, all)} of all)` : ''}`];
    if (this.series.length > 1) {
      this.series.forEach((s, i) => {
        const v = row.values[i] ?? 0;
        lines.push(`${s.label}: ${v}${total > 0 ? ` (${percent(v, total)})` : ''}`);
      });
    }
    if (isClickable(row) && this.#onRowClick)
      lines.push(row.label === this.#selected ? 'Click to close the list' : 'Click to list the devices');
    return lines.join('\n');
  }

  #rowAt(y: number): number {
    const index = Math.floor((y - this.localRect.y) / ROW_HEIGHT);
    return index >= 0 && index < this.#rows.length ? index : -1;
  }

  #setHover(index: number): void {
    if (index === this.#hover) return;
    this.#hover = index;
    this.invalidate();
  }

  #grandTotal(): number {
    return this.#rows.reduce((n, r) => n + sum(r.values), 0);
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const c = crm(theme);
    const r = this.localRect;
    if (this.#rows.length === 0) return;

    let labelWidth = 0;
    // Measured in the selected weight, so marking a row never cuts its label short.
    for (const row of this.#rows) labelWidth = Math.max(labelWidth, rc.measureWidth(row.label, SELECTED_FONT));
    labelWidth = Math.min(Math.ceil(labelWidth), Math.floor(r.width * LABEL_SHARE));

    let valueWidth = 0;
    for (const row of this.#rows) valueWidth = Math.max(valueWidth, rc.measureWidth(String(sum(row.values)), FONT.small));
    valueWidth = Math.ceil(valueWidth);

    const trackX = r.x + labelWidth + GAP.md;
    const trackWidth = Math.max(0, r.width - labelWidth - GAP.md - GAP.sm - valueWidth);
    const peak = Math.max(1, ...this.#rows.map(row => sum(row.values)));
    const colors: Color[] = this.series.map(s => c[s.color]);

    this.#rows.forEach((row, i) => {
      const top = r.y + i * ROW_HEIGHT;
      const mid = top + ROW_HEIGHT / 2;
      const selected = row.label === this.#selected;
      if (selected || i === this.#hover)
        rc.fillRect({ x: r.x, y: top, width: r.width, height: ROW_HEIGHT }, c.altSurface, 4);

      rc.drawText(row.label, r.x, mid, selected ? SELECTED_FONT : FONT.small, selected ? c.text : c.subtleText, {
        baseline: 'middle',
        maxWidth: labelWidth
      });

      const total = sum(row.values);
      const barTop = mid - BAR_HEIGHT / 2;
      // A faint track, so a zero row still reads as "nothing" rather than as a missing row.
      rc.fillRect({ x: trackX, y: barTop, width: trackWidth, height: BAR_HEIGHT }, c.altSurface, BAR_HEIGHT / 2);

      let x = trackX;
      const end = trackX + (total / peak) * trackWidth;
      row.values.forEach((v, s) => {
        if (v <= 0) return;
        const width = (v / peak) * trackWidth;
        const isLast = row.values.slice(s + 1).every(rest => rest <= 0);
        const drawn = Math.max(2, isLast ? end - x : width - SEGMENT_GAP);
        rc.fillRect({ x, y: barTop, width: drawn, height: BAR_HEIGHT }, colors[s] ?? c.brand, Math.min(4, drawn / 2));
        x += width;
      });

      rc.drawText(String(total), end + GAP.sm, mid, FONT.small, c.text, { baseline: 'middle' });
    });
  }
}

function isClickable(row: BarRow): boolean {
  return row.deviceIds.length > 0;
}

function sum(values: readonly number[]): number {
  return values.reduce((n, v) => n + v, 0);
}

function percent(part: number, whole: number): string {
  return `${Math.round((part / whole) * 100)}%`;
}

/** An 8px square in a series colour, for a legend beside the chart. */
export class LegendSwatch extends Control {
  readonly #color: keyof CrmColors;

  constructor(color: keyof CrmColors) {
    super({ width: 8, height: 8 });
    this.#color = color;
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    rc.fillRect(this.localRect, crm(theme)[this.#color], 2);
  }
}
